import { cn } from "@/lib/utils";
import type {
  Priority,
  MatrixItemStatus,
  DocumentStatus,
  QASeverity,
  ProjectStatus,
} from "@/lib/api";

const priorityConfig: Record<Priority, { label: string; className: string }> = {
  high: { label: "High", className: "bg-red-500/15 text-red-400 border border-red-500/20" },
  medium: {
    label: "Medium",
    className: "bg-yellow-500/15 text-yellow-400 border border-yellow-500/20",
  },
  low: { label: "Low", className: "bg-gray-500/15 text-gray-400 border border-gray-500/20" },
};

const statusConfig: Record<MatrixItemStatus, { label: string; className: string }> = {
  not_started: {
    label: "Not Started",
    className: "bg-gray-500/15 text-gray-400 border border-gray-500/20",
  },
  in_progress: {
    label: "In Progress",
    className: "bg-blue-500/15 text-blue-400 border border-blue-500/20",
  },
  draft_ready: {
    label: "Draft Ready",
    className: "bg-purple-500/15 text-purple-400 border border-purple-500/20",
  },
  in_review: {
    label: "In Review",
    className: "bg-orange-500/15 text-orange-400 border border-orange-500/20",
  },
  complete: {
    label: "Complete",
    className: "bg-green-500/15 text-green-400 border border-green-500/20",
  },
};

const docStatusConfig: Record<DocumentStatus, { label: string; className: string }> = {
  not_started: {
    label: "Not Started",
    className: "bg-gray-500/15 text-gray-400 border border-gray-500/20",
  },
  in_progress: {
    label: "In Progress",
    className: "bg-blue-500/15 text-blue-400 border border-blue-500/20",
  },
  complete: {
    label: "Complete",
    className: "bg-green-500/15 text-green-400 border border-green-500/20",
  },
  not_required: {
    label: "Not Required",
    className: "bg-gray-500/10 text-gray-500 border border-gray-500/10",
  },
};

const qaConfig: Record<QASeverity, { label: string; className: string }> = {
  critical: { label: "Critical", className: "bg-red-500/15 text-red-400 border border-red-500/20" },
  warning: {
    label: "Warning",
    className: "bg-yellow-500/15 text-yellow-400 border border-yellow-500/20",
  },
  suggestion: {
    label: "Suggestion",
    className: "bg-blue-500/15 text-blue-400 border border-blue-500/20",
  },
};

const projectStatusConfig: Record<ProjectStatus, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-gray-500/15 text-gray-400 border border-gray-500/20" },
  active: { label: "Active", className: "bg-blue-500/15 text-blue-400 border border-blue-500/20" },
  submitted: {
    label: "Submitted",
    className: "bg-purple-500/15 text-purple-400 border border-purple-500/20",
  },
  won: { label: "Won", className: "bg-green-500/15 text-green-400 border border-green-500/20" },
  lost: { label: "Lost", className: "bg-red-500/15 text-red-400 border border-red-500/20" },
};

interface BadgeProps {
  className?: string;
  children: React.ReactNode;
}

export function Badge({ className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: Priority }) {
  const config = priorityConfig[priority] ?? priorityConfig.medium;
  return <Badge className={config.className}>{config.label}</Badge>;
}

export function StatusBadge({ status }: { status: MatrixItemStatus }) {
  const config = statusConfig[status] ?? statusConfig.not_started;
  return <Badge className={config.className}>{config.label}</Badge>;
}

export function DocStatusBadge({ status }: { status: DocumentStatus }) {
  const config = docStatusConfig[status] ?? docStatusConfig.not_started;
  return <Badge className={config.className}>{config.label}</Badge>;
}

export function QABadge({ severity }: { severity: QASeverity }) {
  const config = qaConfig[severity] ?? qaConfig.suggestion;
  return <Badge className={config.className}>{config.label}</Badge>;
}

export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const config = projectStatusConfig[status] ?? projectStatusConfig.draft;
  return <Badge className={config.className}>{config.label}</Badge>;
}

const typeColors: Record<string, string> = {
  strategy: "bg-indigo-500/15 text-indigo-400 border border-indigo-500/20",
  proof: "bg-teal-500/15 text-teal-400 border border-teal-500/20",
  pricing: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20",
  operations: "bg-cyan-500/15 text-cyan-400 border border-cyan-500/20",
  compliance: "bg-orange-500/15 text-orange-400 border border-orange-500/20",
  creative: "bg-pink-500/15 text-pink-400 border border-pink-500/20",
  admin: "bg-gray-500/15 text-gray-400 border border-gray-500/20",
};

export function TypeBadge({ type }: { type: string }) {
  return (
    <Badge className={cn(typeColors[type] ?? typeColors.admin)}>
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </Badge>
  );
}
